import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowCounterClockwise } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import { useReactivateCloudConnectSubscription } from 'src/api/queries';
import { Button, NotificationBox } from 'src/components';
import { NotificationBanner } from 'src/components/NotificationBanner';
import { formatDate } from 'src/utils/formatDate';

export const CloudConnectSubscriptionCanceledNotification = ({
  subscription,
}: {
  subscription: CloudConnectSubscription | null;
}) => {
  const { t, i18n } = useLingui();
  const reactivateSubscription = useReactivateCloudConnectSubscription();

  if (!subscription?.cancelAtPeriodEnd) {
    return null;
  }

  const cancellationDate = formatDate(
    subscription.cancelAt ?? subscription.periodEnd,
    { locale: i18n.locale }
  );

  return (
    <NotificationBanner>
      <div className="flex flex-col gap-16">
        <Typography variant="heading-subtle-lg" className="my-0">
          <Trans>Your subscription ends {cancellationDate}</Trans>
        </Typography>

        <Typography variant="interface-md" className="my-0 leading-150">
          <Trans>
            You will lose access to the included features, and you&apos;ll no
            longer be charged. Undo your cancelation to keep Connect.
          </Trans>
        </Typography>

        {reactivateSubscription.isError && (
          <NotificationBox
            error={reactivateSubscription.error}
            title={t`Something went wrong`}
          >
            <Trans>
              We couldn&apos;t undo your cancelation. Please try again later or
              contact support if the problem persists.
            </Trans>
          </NotificationBox>
        )}

        <Button
          variant="tertiary"
          size="medium"
          className="mt-8 w-full xs:w-fit"
          loading={reactivateSubscription.isPending}
          onClick={() => {
            reactivateSubscription.mutate();
          }}
          analytics={{
            text: 'Undo cancelation',
            location: ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
            action: 'undo cancelation',
          }}
        >
          <ArrowCounterClockwise />
          <span>
            <Trans>Undo cancelation</Trans>
          </span>
        </Button>
      </div>
    </NotificationBanner>
  );
};
